// crewbit-admin/src/components/money/RecordPaymentPanel.tsx
//
// §Money.StubActionSweepCompletion Phase 3. Centered card modal for the
// record-payment HeaderQuickActions verb. Standalone payment-recording entry
// (no pre-selected invoice). Picks an outstanding invoice by client/number,
// captures amount + method + date + notes → calls record_manual_invoice_payment.
//
// Method choices restricted to cash/cheque/etransfer/other per the RPC's
// validation (see Migration body — card/debit go through set_invoice_paid_manually
// instead).

import { useMemo, useState } from 'react';
import { DollarSign } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney, parseMoney, isPositive } from '../../lib/money';
import { recordManualInvoicePayment } from '../../lib/recordPaymentMutations';
import { useOutstandingInvoices } from '../../hooks/useOutstandingInvoices';

const METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
] as const;

type Method = typeof METHOD_OPTIONS[number]['value'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecordPaymentPanel({ isOpen, onClose, onResult }: Props) {
  const outstanding = useOutstandingInvoices();
  const rows = useMemo(() => outstanding.data ?? [], [outstanding.data]);

  const [invoiceId, setInvoiceId] = useState<string>('');
  const [amount, setAmount] = useState(canonicalMoney(0));
  const [method, setMethod] = useState<Method>('cash');
  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const selected = useMemo(() => rows.find((r) => r.id === invoiceId) ?? null, [rows, invoiceId]);

  if (!isOpen) return null;

  const valid = !!invoiceId && isPositive(parseMoney(amount));

  const reset = () => {
    setInvoiceId('');
    setAmount(canonicalMoney(0));
    setMethod('cash');
    setDate(todayIso());
    setNotes('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleInvoiceChange = (id: string) => {
    setInvoiceId(id);
    const row = rows.find((r) => r.id === id);
    if (row) setAmount(canonicalMoney(row.total));
  };

  const onConfirm = async () => {
    if (!valid) return;
    setLoading(true);
    const result = await recordManualInvoicePayment({
      invoiceId,
      amount: Number(parseMoney(amount)),
      method,
      receivedAt: new Date(date).toISOString(),
      notes: notes.trim() || null,
    });
    setLoading(false);
    if (result.success) {
      const num = selected?.invoice_number ?? 'invoice';
      onResult(true, result.data.already_paid
        ? `Invoice ${num} was already paid.`
        : `Payment of $${parseMoney(amount)} recorded on ${num}.`);
      reset();
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to record payment.');
    }
  };

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 480, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.greenWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <DollarSign size={22} color={palette.green} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Record payment
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Pick an outstanding invoice and log the payment in the ledger.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <Field label="Invoice" required>
            {outstanding.isLoading ? (
              <div style={{ ...inputStyle, color: palette.g500 }}>Loading…</div>
            ) : rows.length === 0 ? (
              <div style={{ ...inputStyle, color: palette.g500 }}>No outstanding invoices.</div>
            ) : (
              <select
                value={invoiceId}
                onChange={(e) => handleInvoiceChange(e.target.value)}
                style={inputStyle}
              >
                <option value="">Select an invoice…</option>
                {rows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.invoice_number} · {r.client_name ?? 'No client'} · ${r.total.toFixed(2)}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Amount" required>
            <MoneyInput value={amount} onChange={setAmount} ariaLabel="Payment amount" />
          </Field>

          <Field label="Method">
            <select value={method} onChange={(e) => setMethod(e.target.value as Method)} style={inputStyle}>
              {METHOD_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Date received">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Reference number, memo, etc."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.green,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Recording…' : 'Record payment'}</button>
        </div>
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}{required && <span style={{ color: palette.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
